import React, { useEffect, useState, useRef } from "react";
import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import axiosInstance from "../Axios/AxiosInstance";
import ReusableTable from "../Components/Table/ReusableTable";
import { MdAdd, MdDownload, MdClose, MdFilterAltOff } from "react-icons/md";
import DatePicker from "../Components/DatePicker/DatePicker";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from "react-router-dom";
import { BsCalendar3 } from "react-icons/bs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Scheme = () => {
  const [schemeData, setSchemeData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDates, setSelectedDates] = useState({ startDate: '', endDate: '' });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [isActive, setIsActive] = useState();

  const pageDataRef = useRef({});
  const popupRef = useRef(null);
  const navigate = useNavigate();

  const userinfo = JSON.parse(sessionStorage.getItem('userinfo')) || {};
  const employeeId = userinfo.employeeId || 'N/A';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowDatePicker(false)
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatDate = (date) => {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  };

  const formatDateDisplay = (date) => {
    const [year, month, day] = date.split('-');
    return `${day}-${month}-${year}`;
  };


  useEffect(() => {
    if (searchInput === '') {
      pageDataRef.current = {};
      fetchSchemeData();
    }
  }, [searchInput]);

  useEffect(() => {
    fetchSchemeData();
  }, [currentPage, rowsPerPage, searchInput, selectedDates]);

  const fetchSchemeData = () => {
    const cacheKey = `${currentPage}-${rowsPerPage}-${selectedDates.startDate || ""}-${selectedDates.endDate || ""}-${searchInput}`;
    if (pageDataRef.current[cacheKey]) {
      setSchemeData(pageDataRef.current[cacheKey]);
      setIsLoading(false);
    } else {
      setIsLoading(true);
      axiosInstance
        .post("/schemes/GetAllSchemes", {
          fromDate: selectedDates.startDate,
          toDate: selectedDates.endDate,
          pageNumber: currentPage,
          pageSize: rowsPerPage,
          searchItem: searchInput,
        })
        .then((response) => {
          const { schemeData, paginatedData } = response.data;

          const formatDate = (isoDateString) => {
            if (!isoDateString) return '';
            const [year, month, day] = isoDateString.substring(0, 10).split("-");
            return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
          };

          const updateSchemeDataList = schemeData.map((order) => ({
            ...order,
            createdAt: formatDate(order.createdAt),
            startDate: formatDate(order.startDate),
            endDate: formatDate(order.endDate),
          }));

          setSchemeData(updateSchemeDataList);
          setTotalRecords(paginatedData.totalRecords);
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          console.error("Error fetching order data:", error);
        });
    }
  };


  const clearFilters = () => {
    setSelectedDates({ startDate: '', endDate: '' });
    setSearchInput('');
  };

  const columns = [
    { key: "schemeName", label: "Scheme Name" },
    { key: "startDate", label: "Start Date" },
    { key: "endDate", label: "End Date" },
    { key: "createdBy", label: "Created by" },
    { key: "createdAt", label: "Created Date" },
    { key: "category", label: "Category" },
    { key: "isActive", label: "Status" },
  ];

  const handleSelectedRowsChange = (newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handleDateSubmit = (dates) => {
    const formattedDates = {
      startDate: formatDate(dates.startDate),
      endDate: formatDate(dates.endDate),
    };
    setSelectedDates(formattedDates);
    setShowDatePicker(false);
  };

  const handleDownload = () => {
    axiosInstance
      .post('/schemes/GetAllSchemes', {
        fromDate: selectedDates.startDate,
        toDate: selectedDates.endDate,
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        searchItem: searchInput,
        isDownload: true
      }, {
        responseType: 'json',
      })
      .then(response => {
        const data = response.data.schemeData;
        if (!data || data.length === 0) {
          toast.error('There is no data to download');
          return;
        }
  
        if (Array.isArray(data) && data.length > 0) {
          const currentDate = getCurrentDate();
          exportToExcel(data, `Scheme Report ${currentDate}`);
        } else {
          console.error('No data available to export.');
        }
      })
      .catch(error => {
        console.error('Error downloading data:', error);
      });
  };
  

  const exportToExcel = (data, fileName) => {
    const columnMapping = {
      schemeName: "Scheme Name",
      amount: "Amount",
      createdBy: "Created By",
      startDate: "Start Date",
      endDate: "End Date",
      createdAt: "Created At",
      category: "Category",
      categoryValues: "Dealer/State",
      scanSequenceValues: "Scan Sequence",
      timeSpecificValues: "Time Specific",
      materialCodeValues: "Material Code",
      isActive: "Status",
    };
  
    const formatDateTime = (value) => {
      if (!value) return "";
      const date = new Date(value);
      const pad = (n) => n.toString().padStart(2, '0');
      const day = pad(date.getDate());
      const month = pad(date.getMonth() + 1);
      const year = date.getFullYear();
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      return `${day}-${month}-${year}`;
    };
  
    const formattedData = data.map(item => {
      const formatted = {};
  
      for (const key in columnMapping) {
        let value = item[key];
        if (['startDate', 'endDate', 'createdAt'].includes(key)) {
          value = formatDateTime(value);
        } else if (Array.isArray(value)) {
          value = value.join(', ');
        } else if (key === 'isActive') {
          value = value === true ? 'Active' : 'Inactive';
        }
        formatted[columnMapping[key]] = value || '';
      }
  
      return formatted;
    });
  
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${fileName}.xlsx`);
  };
  
  const getCurrentDate = () => {
    const date = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
  };
  

  const handleSearchInputChange = (e) => {
    const newSearchValue = e.target.value;
    setSearchInput(newSearchValue);
    setCurrentPage(1);
  };

  const handleCreateScheme = () => {
    navigate('/CreateScheme');
  };

  const handleDealerFile = () => {
    navigate('/SchemeDealerFile');
  };

  const handleMaterialFile = () => {
    navigate('/SchemeMaterialFile');
  };


  const handleStatusClick = (row) => {
    const { schemeId, isActive } = row;
    setSelectedScheme(schemeId);
    setIsActive(isActive)
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setSelectedScheme(null);
    setIsActive("")
  };

  const handleStatusUpdate = () => {
    if (!selectedScheme) return;
    const schemeId = selectedScheme;
    const loginId = employeeId
    axiosInstance
      .post(`/schemes/UpdateScheme?schemeId=${schemeId}&loginId=${loginId}`, {})
      .then((response) => {
        console.log("Scheme marked as inactive successfully:", response.data);
        toast.success(response.data)
      })
      .catch((error) => {
        console.error("Failed to update scheme status:", error);
      })
      .finally(() => {
        handleCloseStatusModal();
        fetchSchemeData()
      });
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header title={"Scheme"} />
      <div className="flex flex-col flex-1 mx-2 my-2">

        <div className="flex flex-wrap justify-between items-center mt-2 mb-1">
          <div className="flex items-center gap-4 ml-1">
            <input
              type="text"
              placeholder="Search by Scheme Name"
              className="px-4 py-2 border border-gray-300 rounded-lg w-[300px] focus:outline-none focus:ring-2 focus:ring-black-500"
              value={searchInput}
              onChange={handleSearchInputChange}
            />
            {selectedDates.startDate && (
              <div className="ml-2 flex space-x-3">
                <span className="bg-[#1e365e] text-white px-3 py-2 rounded-lg shadow-md">
                  From: {formatDateDisplay(selectedDates.startDate)}
                </span>
                <span className="bg-[#1e365e] text-white px-4 py-2 rounded-lg shadow-md ">
                  To: {formatDateDisplay(selectedDates.endDate)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className="flex items-center gap-1 px-2 py-2 mt-1 text-white text-sm bg-[#1e365e] rounded-lg cursor-pointer"
              onClick={handleDealerFile}
              title="Dealer File"
            >
              Dealer Files
            </span>
            <span
              className="flex items-center gap-1 px-2 py-2 mt-1 text-white text-sm bg-[#1e365e] rounded-lg cursor-pointer"
              onClick={handleMaterialFile}
              title="Dealer File"
            >
              Material Files
            </span>
            <span
              className="flex items-center gap-1 px-2 py-2 mt-1 text-white text-sm bg-[#1e365e] rounded-lg cursor-pointer"
              onClick={handleCreateScheme}
              title="Create Scheme"
            >
              Create Scheme
            </span>
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
              onClick={clearFilters}
              title="Clear Filter"
            >
              <MdFilterAltOff size={32} />
            </span>
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
              onClick={() => setShowDatePicker(!showDatePicker)}
              title="Date Filter"
            >
              <BsCalendar3 size={25} />
            </span>
            <span
              className="text-[#1e365e] cursor-pointer hover:text-[#1e365e]  "
              onClick={handleDownload}
              title="Download"
            >
              <MdDownload size={32} />
            </span>
          </div>
        </div>
        <div className="mt-2 p-1">
          <ReusableTable
            data={schemeData}
            columns={columns}
            rowsPerPage={rowsPerPage}
            loading={isLoading}
            currentPage={currentPage}
            totalRecords={totalRecords}
            enableSchemeStaus={true}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSelectedRowsChange={handleSelectedRowsChange}
            onStatusClick={handleStatusClick}
          />
        </div>
        {showDatePicker && (
          <dialog open
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              border: "none",
              background: "none",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
            ref={popupRef}>
            <div >
              <DatePicker onCancel={() => setShowDatePicker(false)} onSubmit={handleDateSubmit} />
            </div>
          </dialog>
        )}
        {showStatusModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold">Update Status</h2>
                <button onClick={handleCloseStatusModal}>
                  <MdClose size={24} />
                </button>
              </div>
              <p className="text-center text-gray-700 font-medium mb-6">
                {isActive ? (
                  <>
                    Do you want to mark this scheme as{" "}
                    <span className="text-red-600 font-semibold">Inactive</span>?
                  </>
                ) : (
                  <span className="text-red-600 font-semibold">
                    The scheme is already Inactive
                  </span>
                )}
              </p>

              {isActive ? (<div className="flex justify-center gap-8">
                <button
                  className={`px-4 py-2 rounded ${isActive ? "bg-[#1e365e] text-white" : "bg-gray-400 text-white cursor-not-allowed"
                    }`}
                  onClick={handleStatusUpdate}
                  disabled={!isActive}
                >
                  Yes
                </button>
                <button
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                  onClick={handleCloseStatusModal}
                >
                  No
                </button>
              </div>):
            <div className="flex justify-center gap-8">
            <button
              className="bg-gray-500 text-white px-4 py-2 rounded"
              onClick={handleCloseStatusModal}
            >
              Cancel
            </button>
          </div>}
            </div>
          </div>
        )}
      </div>
      <Footer />
      <ToastContainer />
    </div>
  );
};

export default Scheme;
